import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EditorColumnHeader } from "../editor_columns/EditorColumn";
import {
  selectAllPropPresets,
  selectSelectedPropPresetId,
  updateSelectedPropPreset,
} from "../../app/slices/TrainingSlice";
import ActorPresetRow from "../actor_presets/ActorPresetRow";
import PiInputfield from "../../components/PiInputField";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import usePropHelper from "../../hooks/PropHelper";
import { Prop, PropType } from "../../models/Prop";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getPropRenderThumbnailCacheKey,
  selectCachedPropRenderThumbnails,
  selectPropCategories,
  setCachedPropRenderThumbnail,
} from "../../app/slices/PropLibrarySlice";
import PropLibraryCategoryLabel from "../prop_library/PropLibraryCategoryLabel";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { PropService } from "../../services/PropService";
import {
  selectCurrentLanguage,
  selectPropLocalizedEntries,
  updatePropLocalizedString,
} from "../../app/slices/TrainingLocalizationSlice";
import usePropLocalizedTranslation from "./PropLocalizedTranslation.hook";
import "./PropPresetEditor.css";

const getRenderRoutePropType = (propType: PropType): 0 | 1 | undefined => {
  if (propType === PropType.Default) return 0;
  if (propType === PropType.Situation) return 1;
  return undefined;
};

const getInspectDisplayName = (name: string) =>
  name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

export default function PropPresetEditor() {
  const selectedPropPreset = useSelector(selectSelectedPropPresetId);
  const propPresets = useSelector(selectAllPropPresets);
  const selectedProp = propPresets.find(
    (prop) => prop.id === selectedPropPreset,
  );
  const { getPropDTO } = usePropHelper();
  const propDTO = selectedProp ? getPropDTO(selectedProp?.propId) : undefined;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const noPrefabNameLabel = String(t("no_prefab_name") ?? "");
  const categories = useSelector(selectPropCategories);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const token = useSelector(selectToken);
  const cachedPropRenderThumbnails = useSelector(
    selectCachedPropRenderThumbnails,
  );
  const currentLanguage = useSelector(selectCurrentLanguage);
  const propLocalizedEntries = useSelector(selectPropLocalizedEntries);
  const cachedPropRenderThumbnailsRef = useRef(cachedPropRenderThumbnails);
  const inFlightThumbnailKeys = useRef(new Set<string>());
  const renderRoutePropType = selectedProp
    ? getRenderRoutePropType(selectedProp.propType)
    : undefined;
  const inspectDefinitions = propDTO?.metadata.inspectDefinitions ?? [];
  const localizedKeys = propDTO?.metadata.localizedKeys ?? [];
  const {
    canTranslateAll,
    canTranslateMissing,
    isTranslating,
    translateAll,
    translateMissing,
  } = usePropLocalizedTranslation(localizedKeys);
  const selectedPropId = selectedProp?.propId;
  const selectedPropInstanceId = selectedProp?.id;

  useEffect(() => {
    cachedPropRenderThumbnailsRef.current = cachedPropRenderThumbnails;
  }, [cachedPropRenderThumbnails]);

  const inspectThumbnailItems = useMemo(() => {
    const seen = new Set<string>();

    return inspectDefinitions.flatMap((inspectDefinition) => {
      const displayName = getInspectDisplayName(inspectDefinition.name);
      const identity = displayName
        ? `name:${displayName.toLowerCase()}`
        : `id:${inspectDefinition.id ?? ""}`;

      if (seen.has(identity)) return [];
      seen.add(identity);

      return [
        {
          description: inspectDefinition.description,
          displayName,
          key: identity,
          renderId: inspectDefinition.id,
        },
      ];
    });
  }, [inspectDefinitions]);

  const inspectThumbnailSignature = useMemo(
    () =>
      inspectThumbnailItems
        .map((item) => `${item.renderId ?? ""}:${item.displayName}`)
        .join("|"),
    [inspectThumbnailItems],
  );

  const myCategoriesMapped = propDTO?.propContent.categories.map((category) => {
    var result = categories.find((cat) => cat.id === category.id);
    if (!result) return;
    return <PropLibraryCategoryLabel category={result} key={result.id} compact />;
  });

  useEffect(() => {
    if (!currentOrganization?.id || !token || !selectedPropId) return;
    if (renderRoutePropType === undefined) return;

    let cancelled = false;
    const loadMissingThumbnails = async () => {
      for (const inspectThumbnailItem of inspectThumbnailItems) {
        if (!inspectThumbnailItem.renderId) continue;

        const cacheKey = getPropRenderThumbnailCacheKey(
          renderRoutePropType,
          selectedPropId,
          inspectThumbnailItem.renderId,
        );
        if (cachedPropRenderThumbnailsRef.current[cacheKey]) continue;
        if (inFlightThumbnailKeys.current.has(cacheKey)) continue;

        inFlightThumbnailKeys.current.add(cacheKey);
        const thumbnail = await PropService.GetPropRenderThumbnail(
          currentOrganization.id,
          token,
          renderRoutePropType,
          selectedPropId,
          inspectThumbnailItem.renderId,
        );
        inFlightThumbnailKeys.current.delete(cacheKey);

        if (cancelled || !thumbnail?.downloadUri) continue;
        cachedPropRenderThumbnailsRef.current = {
          ...cachedPropRenderThumbnailsRef.current,
          [cacheKey]: thumbnail.downloadUri,
        };
        dispatch(
          setCachedPropRenderThumbnail({
            propType: renderRoutePropType,
            propId: selectedPropId,
            renderId: inspectThumbnailItem.renderId,
            downloadUri: thumbnail.downloadUri,
          }),
        );
      }
    };

    loadMissingThumbnails();

    return () => {
      cancelled = true;
    };
  }, [
    currentOrganization?.id,
    dispatch,
    inspectThumbnailItems,
    inspectThumbnailSignature,
    renderRoutePropType,
    selectedPropId,
    token,
  ]);

  const updateSelectedProp = (field: keyof Prop, value: any) => {
    if (!selectedProp) return;
    const cpy: Prop = { ...selectedProp, [field]: value };
    dispatch(updateSelectedPropPreset(cpy));
  };

  const previewUri = propDTO?.propContent.downloadUri;
  const prefabName = selectedProp?.prefabName;

  const copyPrefabName = () => {
    if (prefabName) void navigator.clipboard?.writeText(prefabName);
  };

  const inspectThumbnails =
    inspectThumbnailItems.length > 0 ? (
      <div className="proppreseteditor-inspectgrid" data-testid="prop-inspect-views">
        {inspectThumbnailItems.map((inspectThumbnailItem) => {
          const cacheKey =
            inspectThumbnailItem.renderId &&
            renderRoutePropType !== undefined &&
            selectedPropId
              ? getPropRenderThumbnailCacheKey(
                  renderRoutePropType,
                  selectedPropId,
                  inspectThumbnailItem.renderId,
                )
              : undefined;
          const thumbnailUri = cacheKey
            ? cachedPropRenderThumbnails[cacheKey]
            : undefined;
          // Without a render id there is nothing to fetch; with one, an empty stage is still loading.
          const hasNoRender = !inspectThumbnailItem.renderId;

          return (
            <div
              className={`proppreseteditor-inspectitem${hasNoRender ? " proppreseteditor-inspectitem--missing" : ""}`}
              key={`${selectedPropInstanceId ?? selectedPropId}:${inspectThumbnailItem.key}`}
            >
              <div className="proppreseteditor-inspectstage">
                {thumbnailUri ? (
                  <img
                    className="proppreseteditor-inspectimage"
                    src={thumbnailUri}
                    alt={inspectThumbnailItem.displayName}
                  />
                ) : hasNoRender ? (
                  <div className="proppreseteditor-inspectempty">
                    <ImageNotSupportedOutlinedIcon fontSize="inherit" color="inherit" />
                    <span>{String(t("prop_preset_editor.no_render"))}</span>
                  </div>
                ) : null}
              </div>
              <div className="proppreseteditor-inspectname">
                {inspectThumbnailItem.displayName}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="proppreseteditor-inspectnone">
        {String(t("prop_preset_editor.no_inspect_views"))}
      </div>
    );

  const localizedText = (
    <div className="proppreseteditor-localized">
      <div className="proppreseteditor-localizedfields">
        {localizedKeys.map((key) => (
          <PiInputfield
            key={key}
            label={key}
            onChange={(newValue) =>
              dispatch(
                updatePropLocalizedString({
                  key,
                  shortcode: currentLanguage.shortcode,
                  newValue,
                }),
              )
            }
            placeholder={String(t("localized_text_placeholder") ?? "")}
            value={
              propLocalizedEntries[key]?.[currentLanguage.shortcode]?.value ?? ""
            }
          />
        ))}
      </div>
      <div className="proppreseteditor-localizedactions" aria-busy={isTranslating}>
        {isTranslating ? (
          <div className="proppreseteditor-localizedloading" role="status">
            <CircularProgress color="inherit" size={20} />
          </div>
        ) : (
          <>
            <button
              type="button"
              className="proppreseteditor-button"
              disabled={!canTranslateMissing}
              onClick={() => void translateMissing()}
              data-testid="prop-translate-missing"
            >
              <GTranslateOutlinedIcon fontSize="inherit" className="proppreseteditor-button-icon" />
              {String(t("localization_menu.translate_missing") ?? "")}
            </button>
            <button
              type="button"
              className="proppreseteditor-button"
              disabled={!canTranslateAll}
              onClick={() => void translateAll()}
              data-testid="prop-translate-all"
            >
              {String(t("localization_menu.translate_all") ?? "")}
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="proppreseteditor-shell">
      {selectedProp && (
        <EditorColumnHeader
          icon={<ViewInArOutlinedIcon fontSize="inherit" />}
          title={selectedProp.name}
          testId="properties-header"
        />
      )}
      <main
        aria-label="actor-preset-window-root"
        className="proppreseteditor-scroll pit-creator-scrollbar"
      >
        {selectedProp && (
          <p className="proppreseteditor-intro" data-testid="properties-intro">
            {String(t("prop_preset_editor.meta"))}
          </p>
        )}
        {selectedProp && (
          <div className="proppreseteditor-layout">
            <div className="proppreseteditor-stage" data-testid="prop-preview">
              {previewUri ? (
                <img
                  className="proppreseteditor-previewimage"
                  src={previewUri}
                  alt={"Prop Thumbnail"}
                />
              ) : (
                <div className="proppreseteditor-previewempty">
                  <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
                </div>
              )}
            </div>
            <div className="proppreseteditor-side">
              <section className="proppreseteditor-sheet">
                <ActorPresetRow
                  name={String(t("prop_preset_editor.name"))}
                  hint={String(t("prop_preset_editor.name_hint"))}
                  testId="prop-preset-row-name"
                >
                  <div className="proppreseteditor-field">
                    <PiInputfield
                      label=""
                      onChange={(newValue) => updateSelectedProp("name", newValue)}
                      placeholder="Working name..."
                      value={selectedProp?.name}
                    />
                  </div>
                </ActorPresetRow>
                {localizedKeys.length > 0 && (
                  <ActorPresetRow
                    name={String(t("prop_preset_editor.localized_text"))}
                    hint={String(
                      t("prop_preset_editor.localized_hint", {
                        language: currentLanguage.full || currentLanguage.shortcode,
                      }),
                    )}
                    testId="prop-preset-row-localized-text"
                  >
                    {localizedText}
                  </ActorPresetRow>
                )}
                <ActorPresetRow
                  name={String(t("prop_preset_editor.prefab"))}
                  hint={String(t("prop_preset_editor.prefab_hint"))}
                  testId="prop-preset-row-prefab"
                >
                  <span
                    className={`proppreseteditor-readonly${prefabName ? "" : " proppreseteditor-readonly--empty"}`}
                    title={prefabName || noPrefabNameLabel}
                  >
                    <span className="proppreseteditor-readonly-value">
                      {prefabName || noPrefabNameLabel}
                    </span>
                    {prefabName && (
                      <button
                        type="button"
                        className="proppreseteditor-iconbutton"
                        aria-label={String(t("prop_preset_editor.copy_prefab"))}
                        title={String(t("prop_preset_editor.copy_prefab"))}
                        onClick={copyPrefabName}
                      >
                        <ContentCopyOutlinedIcon fontSize="inherit" />
                      </button>
                    )}
                  </span>
                </ActorPresetRow>
                <ActorPresetRow
                  name={String(t("prop_preset_editor.categories"))}
                  testId="prop-preset-row-categories"
                >
                  {myCategoriesMapped?.some(Boolean) ? (
                    <div className="proppreseteditor-chiplist">{myCategoriesMapped}</div>
                  ) : (
                    <span className="proppreseteditor-none">
                      {String(t("prop_preset_editor.no_categories"))}
                    </span>
                  )}
                </ActorPresetRow>
              </section>
              <section className="proppreseteditor-inspect">
                <h2 className="proppreseteditor-sectiontitle">
                  {String(t("prop_preset_editor.inspect_views"))}
                  {inspectThumbnailItems.length > 0 && (
                    <span className="proppreseteditor-sectioncount">
                      {inspectThumbnailItems.length}
                    </span>
                  )}
                </h2>
                {inspectThumbnails}
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
